/**
 * Whether the Google Maps SDK is here, and how to ask for it again.
 *
 * WHY THIS EXISTS. The application loads the Maps script before it boots, and
 * boots anyway when that fails, which is the right call: a member with no
 * network still gets their app. But nothing downstream knew the difference.
 * `new google.maps.places.AutocompleteService()` was called on a global that
 * was not there, so the city screen threw on opening and then threw again on
 * every single keystroke.
 *
 * Measured in production on 20 September 2026, one member, one session, one
 * minute, release mobile-ionic@2.1.7, iOS 18.7:
 *
 *   CAPACITOR-K   ReferenceError: Can't find variable: google      4 events
 *   CAPACITOR-J   undefined is not an object, autocompleteService  39 events
 *
 * Four for opening the screen, thirty-nine for typing a city name into it.
 *
 * WHAT CHANGES. Nothing reaches for the global on its own any more: they ask
 * here. When the script never arrived, asking again loads it, because the
 * reason it failed is usually gone by the time somebody opens the screen: the
 * phone had no network at launch and has one now.
 */
import { environment } from '../../../environments/environment';

/**
 * The one place that knows the SDK's shape, so the rest never guesses.
 *
 * One answer per service rather than a single "is Maps here": the script can
 * be half of what a screen needs, and a screen that geocodes should not be
 * held back by the places library, nor the other way round.
 */
export const googleGeocodingIsLoaded = (): boolean =>
  typeof google !== 'undefined' && !!google.maps?.Geocoder;

export const googlePlacesIsLoaded = (): boolean =>
  typeof google !== 'undefined' && !!google.maps?.places?.AutocompleteService;

/** The script itself has arrived, which is what the loader waits for. */
export const googleMapsIsLoaded = (): boolean =>
  typeof google !== 'undefined' && !!google.maps?.Map && !!google.maps?.places;

/** In flight, so several screens asking at once share one script. */
let loading: Promise<boolean> | undefined;

const alreadyInThePage = (): HTMLScriptElement | null =>
  document.querySelector<HTMLScriptElement>(
    'script[data-google-maps], script[src*="maps.googleapis.com/maps/api/js"]',
  );

const waitFor = (script: HTMLScriptElement): Promise<boolean> =>
  new Promise((resolve) => {
    script.addEventListener('load', () => resolve(googleMapsIsLoaded()), { once: true });
    script.addEventListener('error', () => resolve(false), { once: true });
  });

const addTheScript = (): Promise<boolean> => {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.dataset['googleMaps'] = 'true';
  script.async = true;
  script.defer = true;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleApiKey}&libraries=places`;
  const answer = waitFor(script);
  document.body.appendChild(script);
  return answer;
};

/**
 * Resolves true when the SDK is usable, false when it is not. It NEVER
 * rejects: a caller that has to write a try around this is a caller that will
 * one day forget to.
 *
 * A failed attempt is not remembered. The network is what failed, and it
 * comes back; a screen opened ten minutes later gets a fresh try.
 */
export function ensureGoogleMapsLoaded(): Promise<boolean> {
  if (googleMapsIsLoaded()) {
    return Promise.resolve(true);
  }

  loading ||= (() => {
    const existing = alreadyInThePage();
    // A script tag that is still loading answers on its own events. One that
    // has already failed answers nothing ever again, so it is replaced.
    const answer = existing && !existing.dataset['googleMapsFailed'] ? waitFor(existing) : addTheScript();

    return answer
      .catch(() => false)
      .then((loaded) => {
        loading = undefined;
        if (!loaded) {
          alreadyInThePage()?.setAttribute('data-google-maps-failed', 'true');
        }
        return loaded;
      });
  })();

  return loading;
}
